import { useState, useEffect, useCallback } from "react";
import { Upload, Trash2, LogOut, Copy, Check } from "lucide-react";
import { SEO } from "../components/medskin/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "medskin_admin_token";

const TREATMENT_SLUGS = [
  "hydrafacial",
  "dermapen",
  "microneedling",
  "profhilo",
  "tannbleking",
  "kjemisk-peeling",
  "led-lysbehandling",
  "beroligende-ansiktsbehandling",
  "hydrafacial-sensitive",
  "dyprens",
  "subcision",
  "plasma-skin-tightening",
  "skinbooster",
  "polynukleotider",
  "mesoterapi",
  "prp",
  "filler",
  "sculptra",
  "hifu",
  "vippeloft",
  "brow-lamination",
  "browshape",
  "farging-vipper",
  "vippeekstensjoner",
  "powder-brows",
  "microblading",
  "pmu-refill",
  "pmu-touch-up",
];

function LoginView({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Feil passord");
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F6F3] flex items-center justify-center px-6">
      <div className="w-full max-w-md bg-white border border-[#E5E0DA] rounded-3xl p-10">
        <div className="flex items-center gap-3 mb-6">
          <span className="h-px w-10 bg-[#C4A981]"></span>
          <span className="text-[11px] uppercase tracking-[0.3em] text-[#635D56]">Admin</span>
        </div>
        <h1 className="font-serif text-4xl text-[#2C2C2A] tracking-tight font-light">
          Medskinby <em className="italic text-[#4A4238]">Media</em>
        </h1>
        <p className="mt-3 text-sm text-[#635D56] font-light">Logg inn for å administrere bilder.</p>
        <form onSubmit={submit} className="mt-8 space-y-6" data-testid="admin-login-form">
          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.22em] text-[#635D56] mb-2">Passord</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoFocus
              className="w-full bg-transparent border-b border-[#D1C9C0] focus:border-[#4A4238] outline-none pb-3 text-base text-[#2C2C2A]"
              data-testid="admin-password-input"
            />
          </label>
          {error && (
            <div className="text-sm text-[#8B2E2E] bg-[#F5E6E6] border border-[#8B2E2E]/30 rounded-xl px-4 py-3">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#4A4238] text-[#F7F6F3] hover:bg-[#2C2C2A] disabled:opacity-60 rounded-full px-6 py-3 text-xs uppercase tracking-[0.22em] transition-all"
            data-testid="admin-login-submit"
          >
            {loading ? "Logger inn..." : "Logg inn"}
          </button>
        </form>
      </div>
    </div>
  );
}

function UploadCard({ token, onUploaded }) {
  const [file, setFile] = useState(null);
  const [slug, setSlug] = useState("hydrafacial");
  const [caption, setCaption] = useState("");
  const [alt, setAlt] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setStatus("uploading");
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("treatment_slug", slug);
      fd.append("caption", caption);
      fd.append("alt", alt);
      const res = await fetch(`${API}/admin/upload`, {
        method: "POST",
        headers: { "X-Admin-Password": token },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Opplasting feilet");
      setFile(null);
      setCaption("");
      setAlt("");
      setStatus("idle");
      document.getElementById("file-input").value = "";
      onUploaded();
    } catch (err) {
      setStatus("error");
      setError(err.message);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-[#E5E0DA] rounded-3xl p-8 space-y-5" data-testid="upload-form">
      <div className="flex items-center gap-3">
        <span className="h-px w-8 bg-[#C4A981]"></span>
        <span className="text-[11px] uppercase tracking-[0.25em] text-[#635D56]">Last opp nytt bilde</span>
      </div>

      <label className="block">
        <span className="block text-[11px] uppercase tracking-[0.22em] text-[#635D56] mb-2">Behandling</span>
        <select
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          className="w-full bg-white border border-[#D1C9C0] rounded-xl px-4 py-3 text-sm text-[#2C2C2A]"
          data-testid="upload-slug"
        >
          {TREATMENT_SLUGS.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </label>

      <label className="block">
        <span className="block text-[11px] uppercase tracking-[0.22em] text-[#635D56] mb-2">Bildefil (JPG/PNG/WEBP, maks 12 MB)</span>
        <input
          id="file-input"
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif"
          required
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="w-full text-sm text-[#2C2C2A] file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[#4A4238] file:text-[#F7F6F3] file:cursor-pointer file:text-[11px] file:uppercase file:tracking-[0.22em] hover:file:bg-[#2C2C2A]"
          data-testid="upload-file"
        />
      </label>

      <label className="block">
        <span className="block text-[11px] uppercase tracking-[0.22em] text-[#635D56] mb-2">Caption (vises på bildet)</span>
        <input
          type="text"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          placeholder="F.eks. «Ekte kunde – etter behandling»"
          className="w-full bg-transparent border-b border-[#D1C9C0] focus:border-[#4A4238] outline-none pb-3 text-sm text-[#2C2C2A]"
          data-testid="upload-caption"
        />
      </label>

      <label className="block">
        <span className="block text-[11px] uppercase tracking-[0.22em] text-[#635D56] mb-2">Alt-tekst (SEO/tilgjengelighet)</span>
        <input
          type="text"
          value={alt}
          onChange={(e) => setAlt(e.target.value)}
          placeholder="Beskriv bildet kort"
          className="w-full bg-transparent border-b border-[#D1C9C0] focus:border-[#4A4238] outline-none pb-3 text-sm text-[#2C2C2A]"
          data-testid="upload-alt"
        />
      </label>

      {error && (
        <div className="text-sm text-[#8B2E2E] bg-[#F5E6E6] border border-[#8B2E2E]/30 rounded-xl px-4 py-3">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={status === "uploading" || !file}
        className="inline-flex items-center gap-3 bg-[#4A4238] text-[#F7F6F3] hover:bg-[#2C2C2A] disabled:opacity-60 disabled:cursor-not-allowed rounded-full px-6 py-3 text-xs uppercase tracking-[0.22em] transition-all"
        data-testid="upload-submit"
      >
        <Upload size={14} strokeWidth={1.5} />
        {status === "uploading" ? "Laster opp..." : "Last opp"}
      </button>
    </form>
  );
}

function MediaCard({ item, token, onDeleted }) {
  const [copied, setCopied] = useState(false);
  const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${item.url}`;

  const copyUrl = () => {
    navigator.clipboard.writeText(fullUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  const del = async () => {
    if (!window.confirm("Slette dette bildet?")) return;
    await fetch(`${API}/admin/media/${item.id}`, {
      method: "DELETE",
      headers: { "X-Admin-Password": token },
    });
    onDeleted();
  };

  return (
    <div className="bg-white border border-[#E5E0DA] rounded-2xl overflow-hidden" data-testid={`media-card-${item.id}`}>
      <div className="aspect-[4/3] bg-[#EBE5DF]/40 overflow-hidden">
        <img src={fullUrl} alt={item.alt} className="w-full h-full object-cover" />
      </div>
      <div className="p-4 space-y-2">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#C4A981]">{item.treatment_slug}</div>
        {item.caption && <div className="text-sm text-[#2C2C2A] font-light">{item.caption}</div>}
        <div className="flex items-center gap-2 pt-2">
          <button
            onClick={copyUrl}
            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[#635D56] hover:text-[#4A4238]"
            data-testid={`copy-url-${item.id}`}
          >
            {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Kopiert" : "Kopier URL"}
          </button>
          <button
            onClick={del}
            className="ml-auto inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[#8B2E2E] hover:text-[#5a1e1e]"
            data-testid={`delete-${item.id}`}
          >
            <Trash2 size={12} /> Slett
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/media`, { headers: { "X-Admin-Password": token } });
      if (res.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        return;
      }
      const data = await res.json();
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (!token) return <LoginView onLogin={setToken} />;

  return (
    <div className="min-h-screen bg-[#F7F6F3] pt-8 pb-24" data-testid="admin-dashboard">
      <SEO title="Medskinby Admin – Media" description="" canonical="" />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between mb-10">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#635D56]">Admin · Media</div>
            <h1 className="font-serif text-4xl md:text-5xl text-[#2C2C2A] tracking-tight font-light mt-2">
              Kundebilder & galleri
            </h1>
          </div>
          <button
            onClick={logout}
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#635D56] hover:text-[#4A4238]"
            data-testid="admin-logout"
          >
            <LogOut size={14} strokeWidth={1.5} /> Logg ut
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5">
            <UploadCard token={token} onUploaded={load} />
          </div>
          <div className="lg:col-span-7">
            <div className="flex items-center justify-between mb-5">
              <div className="text-[11px] uppercase tracking-[0.25em] text-[#635D56]">Opplastede bilder</div>
              <div className="text-[11px] text-[#635D56]">{items.length} total</div>
            </div>
            {loading && <div className="text-sm text-[#635D56]">Laster...</div>}
            {!loading && items.length === 0 && (
              <div className="bg-white border border-dashed border-[#D1C9C0] rounded-2xl p-10 text-center text-sm text-[#635D56]">
                Ingen bilder ennå. Last opp ditt første over.
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {items.map((it) => (
                <MediaCard key={it.id} item={it} token={token} onDeleted={load} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
